import type { ISnapshotBody } from '../apis/logistics.js';
import type { InventoryLevel } from '../interfaces/ILogistics.js';

export interface IMergeSnapshotInput {
  /** The user's own count of the old stock. */
  remaining: number;
  /** What the delivery being saved brought in. */
  arrived: number;
  level: Pick<InventoryLevel, 'materialName' | 'unit' | 'amount' | 'spanDays' | 'dailyRate' | 'suggestedSpanDays'>;
}

/**
 * The sync snapshot a delivery save posts when it folds the arrival into the
 * running stock — one rule for web and mobile.
 *
 * Measured cycles beat the entered guess, exactly as the stock dialog's own
 * merge does — otherwise the same material gets one span through this path and
 * a different one through the inventory row. `dailyRate` arrives rounded to
 * 1dp, so a slow material (under 0.05/day) reads as 0; dividing by it turned
 * the span into Infinity, silently clamped to a stored 365. With no usable rate
 * the current span is carried forward — the one figure that is at least the
 * user's own.
 */
export function buildMergeSnapshotBody({ remaining, arrived, level }: IMergeSnapshotInput): ISnapshotBody {
  const amount = Math.round((remaining + arrived) * 10) / 10;
  const rate = level.suggestedSpanDays ? level.amount / level.suggestedSpanDays : level.dailyRate;
  return {
    materialName: level.materialName,
    unit: level.unit,
    amount,
    spanDays: rate > 0 ? Math.max(1, Math.min(365, Math.round(amount / rate))) : level.spanDays,
    mode: 'sync',
    remaining,
  };
}
